import { Download, FileSpreadsheet, Info } from 'lucide-react';

import { BulkFileImport } from '@/components/ui/bulk-file-import';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';

import type {
  BulkUploadFileResponse,
  UploadApiResponse,
} from '../hooks/useBulkImport';

interface BulkImportUploadProps {
  isProperty: boolean;
  fileName: string;
  onTemplateDownload: () => void;
  onFileUpload: (response: UploadApiResponse) => void;
  onFileUploadForOrder: (response: BulkUploadFileResponse) => void;
  setShowStep3: ((v: boolean) => void) | null;
  setFileUploadDisabled: (v: boolean) => void;
  fileUploadDisabled: boolean;
  setInProgress: (v: boolean) => void;
  inProgress: boolean;
  setDisabledUploadProperty: (v: boolean) => void;
  disabledUploadProperty: boolean;
}

export function BulkImportUpload({
  isProperty,
  fileName,
  onTemplateDownload,
  onFileUpload,
  onFileUploadForOrder,
  setShowStep3,
  setFileUploadDisabled,
  fileUploadDisabled,
  setInProgress,
  inProgress,
  setDisabledUploadProperty,
  disabledUploadProperty,
}: BulkImportUploadProps) {
  const entity = isProperty ? 'Property' : 'Order';
  const templateTitle = isProperty
    ? 'XLSX Property Import Template'
    : 'XLSX Bulk Order Template';
  const guidanceCopy = isProperty
    ? 'For guidance, refer to the comments in the spreadsheet or the FAQ section on bulk property import.'
    : 'For guidance, refer to the comments in the spreadsheet or the FAQ section on bulk orders.';
  const step2Copy = isProperty
    ? 'Step 2 - Upload File with Property Data'
    : 'Step 2 - Upload Spreadsheet with Order Details';

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-3 lg:grid-cols-2 lg:gap-x-8">
      <div className="flex items-center gap-2 lg:col-start-1 lg:row-start-1">
        <Typography as="h2" variant="heading-md" className="font-semibold">
          Step 1 - Download Bulk {entity} Template
        </Typography>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Hover/focus-only tooltip trigger — unreachable on touch, so
                  below lg it hides and the guidance renders inline instead. */}
              <button
                type="button"
                aria-label="Template Guidance"
                className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 max-lg:hidden"
              >
                <Info className="h-4 w-4" aria-hidden="true" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              {guidanceCopy}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <Typography as="p" variant="small" className="text-muted-foreground lg:hidden">
        {guidanceCopy}
      </Typography>

      <button
        type="button"
        onClick={onTemplateDownload}
        className={cn(
          'group flex cursor-pointer items-center gap-3 rounded-md border border-border bg-card px-4 py-3 text-left transition-colors',
          'hover:border-primary/40 hover:bg-primary/[0.04]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25',
          'lg:col-start-1 lg:row-start-2 lg:h-[140px] lg:flex-col lg:justify-center lg:gap-2 lg:px-6 lg:text-center',
        )}
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary lg:h-9 lg:w-9">
          <FileSpreadsheet className="h-5 w-5" aria-hidden="true" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col lg:flex-initial lg:items-center lg:gap-0.5">
          <Typography as="span" variant="body-semibold" className="truncate">
            {templateTitle}
          </Typography>
          <Typography as="span" variant="small" className="truncate text-muted-foreground">
            {fileName}.xlsx
          </Typography>
        </span>
        <span className="inline-flex shrink-0 items-center gap-1.5 text-primary">
          <Download className="h-4 w-4" aria-hidden="true" />
          <Typography
            as="span"
            variant="small-semibold"
            className="hidden group-hover:underline lg:inline"
          >
            Download template
          </Typography>
        </span>
      </button>

      <Typography
        as="h2"
        variant="heading-md"
        className="font-semibold lg:col-start-2 lg:row-start-1"
      >
        {step2Copy}
      </Typography>

      <div className="lg:col-start-2 lg:row-start-2">
        <BulkFileImport
          onFileUpload={(response) => onFileUpload(response as UploadApiResponse)}
          onFileUploadForOrder={(response) =>
            onFileUploadForOrder(response as BulkUploadFileResponse)
          }
          setShowStep={!isProperty ? setShowStep3 : null}
          isProperty={isProperty}
          setFileUploadDisabled={setFileUploadDisabled}
          fileUploadDisabled={fileUploadDisabled}
          setInProgress={setInProgress}
          inProgress={inProgress}
          setDisabledUploadProperty={setDisabledUploadProperty}
          disabledUploadProperty={disabledUploadProperty}
          url={
            isProperty
              ? '/property-service/api/v1/properties/bulk'
              : '/order-service/api/v1/orders/bulk/upload'
          }
        />
      </div>
    </div>
  );
}
